/**
 * What a paste into the composer turns into when it carries pictures.
 *
 * The decision this makes is «text or media», and it is the whole of the
 * feature: a clipboard routinely holds both flavours of the same thing, and
 * which one a messenger picks is the difference between pasting a table out of
 * Excel and pasting a screenshot of it. The rule here is that **text wins
 * whenever there is any** — Word, Excel, Google Docs and every rich editor put
 * a rendered `image/png` on the clipboard beside their text, so a handler that
 * looks at the files first turns every pasted paragraph into a picture of a
 * paragraph. A screenshot, a copied picture from a page, «Копировать
 * изображение» in another app: those carry no text flavour at all, and that
 * absence is the signal.
 *
 * Kept here rather than in the composer for the usual reason — it is arithmetic
 * over a clipboard, testable without a DOM, and the composer already has more
 * than enough of its own.
 */

import { albumKindOf } from './album.ts';
import { baseMime } from './capture.ts';

/**
 * The part of a `DataTransfer` this reads.
 *
 * Narrow on purpose: a real one cannot be constructed outside a browser, and
 * these two members are all the decision needs, so the test builds the
 * clipboard it wants to describe instead of a DOM to hold it.
 */
export interface Pasteboard {
  getData(type: string): string;
  files: ArrayLike<File>;
  /**
   * The other place a browser keeps the same thing. Not a duplicate of `files`
   * in practice: engines have shipped with a picture in one and not the other,
   * and reading both costs a loop.
   */
  items?: ArrayLike<{ kind: string; getAsFile(): File | null }>;
}

/**
 * `.png`, `.jpg` — a hint for the server, which names what it stores itself but
 * does carry a plausible extension over (see safeStoredName in
 * server/src/uploads.ts, and sharp behind it).
 *
 * Not capture.ts's version of this, which is about recordings: that one maps
 * `mpeg` to `mp3` and has no opinion about `jpeg`, and the two tables have no
 * reason to agree.
 */
function extensionFor(mime: string): string {
  const subtype = baseMime(mime).split('/')[1] ?? 'bin';
  return subtype === 'jpeg' ? 'jpg' : subtype;
}

/**
 * A name for a pasted file, in the shape the camera's own files already have
 * (see photoFileName in lib/viewfinder.ts): dated, so a thread of pasted
 * screenshots is not ten files called the same thing.
 *
 * The clipboard's own name is kept when it looks like one — a picture copied in
 * a file manager arrives with the name somebody gave it, and that name is worth
 * more than a timestamp. What is thrown away is the browsers' placeholder:
 * every one of them calls a bitmap off the clipboard `image.png`, which says
 * nothing and collides with itself.
 */
export function pastedName(file: { name?: string; type: string }, kind: 'photo' | 'video', at: Date): string {
  const given = file.name ?? '';
  const dot = given.lastIndexOf('.');
  const stem = dot > 0 ? given.slice(0, dot) : given;
  const extension = dot > 0 ? given.slice(dot).toLowerCase() : '';
  const own = stem !== '' && stem.toLowerCase() !== 'image' && /^\.[a-z0-9]{1,8}$/.test(extension);
  if (own) return given;

  const pad = (value: number) => String(value).padStart(2, '0');
  const date = `${at.getFullYear()}${pad(at.getMonth() + 1)}${pad(at.getDate())}`;
  const time = `${pad(at.getHours())}${pad(at.getMinutes())}${pad(at.getSeconds())}`;
  return `${kind === 'video' ? 'video' : 'photo'}-${date}-${time}.${extensionFor(file.type)}`;
}

/** Everything the clipboard offers as a file, from both places it keeps them. */
function clipboardFiles(board: Pasteboard): File[] {
  const found = Array.from(board.files);
  for (const item of Array.from(board.items ?? [])) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    // By size and name rather than by identity: the two lists are built
    // separately and the same picture is two objects in them.
    if (file && !found.some((seen) => seen.name === file.name && seen.size === file.size)) {
      found.push(file);
    }
  }
  return found;
}

/**
 * The pictures and clips on the clipboard, named and ready for the album — or
 * nothing at all, which means «this paste was text» and leaves it to the
 * handler that inserts text.
 *
 * Whatever comes back goes through addToAlbum like a pick from the gallery
 * does, so the cap, the compose screen and the grouping are the same code: a
 * pasted screenshot is a photograph that happened to arrive by another door.
 */
export function pastedMedia(board: Pasteboard, at: Date = new Date()): File[] {
  if (board.getData('text/plain').trim() !== '') return [];

  const out: File[] = [];
  for (const file of clipboardFiles(board)) {
    const kind = albumKindOf(file);
    if (kind === null) continue;
    // A new File rather than a rename in place: `name` is read-only, and the
    // bytes are shared rather than copied, so this costs nothing.
    out.push(new File([file], pastedName(file, kind, at), {
      type: file.type,
      lastModified: file.lastModified,
    }));
  }
  return out;
}

/**
 * A picture the browser's own paste put into the field, as bytes.
 *
 * This is the path Android takes, and it is why it exists. A WebView hands a
 * pasted picture over as markup rather than as a file — `clipboardData.files`
 * is empty and the default paste drops an `<img>` into the contentEditable —
 * which used to leave the picture wedged in the input: `serialise` only
 * understands the smileys' images, so the message read as empty, the send
 * button lit up and pressing it did nothing at all.
 *
 * Two ways in, in order of what they cost the picture. `fetch` gives the
 * original bytes back and is what a `data:` or `blob:` source answers; a source
 * only the renderer can read (`content:`, a remote address behind CORS) refuses
 * it, and then the pixels already on screen are re-encoded off a canvas. A
 * canvas the source has tainted throws, and that is the end of it — null here
 * means the picture cannot be recovered, and the caller has nothing to send.
 */
export async function bytesBehind(image: HTMLImageElement): Promise<Blob | null> {
  try {
    const response = await fetch(image.src);
    if (response.ok) {
      const blob = await response.blob();
      if (blob.size > 0 && blob.type.startsWith('image/')) return blob;
    }
  } catch {
    // Falls through to the canvas, which is the whole point of having one.
  }

  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (width === 0 || height === 0) return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')?.drawImage(image, 0, 0);
    return await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  } catch {
    return null;
  }
}

/** The same naming as a pasted file gets, for bytes that arrived without one. */
export function fileFromBlob(blob: Blob, at: Date = new Date()): File {
  return new File([blob], pastedName({ type: blob.type }, 'photo', at), { type: blob.type });
}
